import { MessagesState } from './message-selectors';
import { FAILURE, STARTING, SUCCESS } from '../action/util/action-name-creators';

import {
  Action,
  deleteResourceByParam,
  replaceResourceByParam,
  setStateDirtyByParam,
  setStatusDirtyById,
  updateResourceByParam,
  updateStatusById,
} from './utils/state-manager';
import { convertArrayToMap } from '../util/convert-array-to-map';

const InitialState: MessagesState = {
  messagesById: {},
  messagesStateByJobId: {},
  messagesStateByOrgId: {},
  announcementsByOrgId: {},
  announcementsByOrgIdStatus: {},
  announcementDeliverySummaryById: {},
  announcementDeliverySummaryByIdStatus: {},
  messagesByJobIdStatus: {},
  employeeMessagesByOrgId: {},
  employeeMessagesByOrgIdStatus: {},
};

const jobApplicantJobMessageKey = (message: JobApplicantJobMessage) =>
  `${message.jobApplicantId}-${message.messageId}`;

export default (state = InitialState, action: Action) => {
  switch (action.type) {
    case STARTING('LOAD_ORG_ANNOUNCEMENTS'): {
      const { orgId } = action.payload.args;
      return updateStatusById(state, 'announcementsByOrgId', true, orgId);
    }
    case SUCCESS('LOAD_ORG_ANNOUNCEMENTS'): {
      const { args, data } = action.payload;
      return updateStatusById(state, 'announcementsByOrgId', false, args.orgId, data);
    }
    case STARTING('LOAD_ANNOUNCEMENT_DELIVERY_SUMMARY'): {
      const { announcementIds } = action.payload.args;
      let newState = { ...state };
      announcementIds.forEach((id: string) => {
        newState = updateStatusById(newState, 'announcementDeliverySummaryById', true, id);
      });

      return newState;
    }
    case SUCCESS('LOAD_ANNOUNCEMENT_DELIVERY_SUMMARY'): {
      const { data: summaries } = action.payload;

      let newState = { ...state };
      summaries.forEach((summary: AnnouncementDeliverySummary) => {
        newState = updateStatusById(
          newState,
          'announcementDeliverySummaryById',
          false,
          summary.announcementId.toString(),
          summary,
        );
      });

      return newState;
    }
    case FAILURE('LOAD_ANNOUNCEMENT_DELIVERY_SUMMARY'): {
      const { announcementIds } = action.payload.args;
      let newState = { ...state };
      announcementIds.forEach((id: string) => {
        newState = updateStatusById(newState, 'announcementDeliverySummaryById', false, id);
      });

      return newState;
    }
    case STARTING('LOAD_ORG_APPLICANT_MESSAGES'): {
      const { orgId } = action.payload.args;
      return updateResourceByParam(state, 'messages', 'orgId', orgId, true);
    }
    case SUCCESS('LOAD_ORG_APPLICANT_MESSAGES'): {
      const { args, data } = action.payload;
      return replaceResourceByParam(
        state,
        'messages',
        'orgId',
        args.orgId,
        convertArrayToMap(data, jobApplicantJobMessageKey),
      );
    }
    case STARTING('LOAD_JOB_MESSAGES'): {
      const { jobId } = action.payload.args;
      return updateResourceByParam(state, 'messages', 'jobId', jobId, true);
    }
    case SUCCESS('LOAD_JOB_MESSAGES'): {
      const { args, data } = action.payload;
      return replaceResourceByParam(
        state,
        'messages',
        'jobId',
        args.jobId,
        convertArrayToMap(data, jobApplicantJobMessageKey),
      );
    }
    case STARTING('LOAD_EMPLOYEE_MESSAGES'): {
      const { orgId } = action.payload.args;
      return updateStatusById(state, 'employeeMessagesByOrgId', true, orgId);
    }
    case SUCCESS('LOAD_EMPLOYEE_MESSAGES'): {
      const { args, data } = action.payload;
      return updateStatusById(state, 'employeeMessagesByOrgId', false, args.orgId, data);
    }
    case SUCCESS('CREATE_ANNOUNCEMENT'): {
      const { orgId } = action.payload.args;
      return setStatusDirtyById(state, 'announcementsByOrgId', orgId);
    }
    case SUCCESS('UPDATE_EMPLOYEE_MESSAGE'): {
      const { orgId } = action.payload.args;
      return setStatusDirtyById(state, 'employeeMessagesByOrgId', orgId);
    }
    case SUCCESS('UPDATE_JOB_MESSAGE'): {
      const { data } = action.payload;

      // This is a bit of craziness - when we dismiss a message, it's possible that multiple contacts have
      // a reference to it. So we need to go over each of our keys and find the messages that have the same
      // message id, and then update each
      const ids: Array<string> = [];
      const clickedMessageKey = jobApplicantJobMessageKey(data);
      Object.keys(state.messagesById).forEach((messageKey) => {
        if (messageKey.endsWith(`-${data.messageId}`)) {
          ids.push(messageKey);
        }
      });

      let newState = { ...state };
      ids.forEach((id) => {
        let thisIdsData = { ...data };
        if (clickedMessageKey !== id) {
          thisIdsData = {
            // @ts-expect-error id is a string and the state definition requires number but this works either way in runtime
            ...state.messagesById[id],
            isDismissed: data.isDismissed,
            unread: data.unread,
          };
        }

        if (data.isDismissed) {
          newState = deleteResourceByParam(newState, 'messages', 'id', id, id);
        }
        newState = updateResourceByParam(newState, 'messages', 'id', id, false, {
          [id]: thisIdsData,
        });
      });
      return newState;
    }
    case 'SET_EMPLOYEE_ORG_MESSAGES_DIRTY': {
      const { orgId } = action.payload;
      return setStatusDirtyById(state, 'employeeMessagesByOrgId', orgId);
    }
    case 'SET_JOB_APPLICANT_JOB_MESSAGES_DIRTY': {
      const { jobId } = action.payload;
      return setStateDirtyByParam(state, 'messages', 'jobId', jobId);
    }
    case 'SET_ORG_JOB_APPLICANT_MESSAGES_DIRTY': {
      const { orgId } = action.payload;
      return setStateDirtyByParam(state, 'messages', 'orgId', orgId);
    }
    case 'SET_DIRTY_ON_TAB_REGAIN_VISIBILITY': {
      let newState = { ...state };
      const employeeMessages = newState.employeeMessagesByOrgId;
      Object.keys(employeeMessages).forEach((orgId) => {
        newState = setStatusDirtyById(newState, 'employeeMessagesByOrgId', orgId);
      });
      const jobApplicantJobMessages = newState.messagesStateByJobId;
      Object.keys(jobApplicantJobMessages).forEach((jobId) => {
        newState = setStateDirtyByParam(newState, 'messages', 'jobId', jobId);
      });
      const orgApplicantJobMessages = newState.messagesStateByOrgId;
      Object.keys(orgApplicantJobMessages).forEach((orgId) => {
        newState = setStateDirtyByParam(newState, 'messages', 'orgId', orgId);
      });
      return newState;
    }
    case SUCCESS('DELETE_EMPLOYEE_GROUP'):
    case SUCCESS('UPDATE_EMPLOYEE_GROUP'): {
      const { orgId } = action.payload.args;
      return setStatusDirtyById(state, 'announcementsByOrgId', orgId);
    }
    case SUCCESS('UPDATE_ANNOUNCEMENT'): {
      const { orgId } = action.payload.args;
      return setStatusDirtyById(state, 'announcementsByOrgId', orgId);
    }
    case SUCCESS('DELETE_EMPLOYEE'): {
      const { orgId } = action.payload.args;
      return setStatusDirtyById(state, 'employeeMessagesByOrgId', orgId);
    }
    default:
      return state;
  }
};
